import React, { useMemo, useState } from 'react';
import { useAI } from '../../context/AIContext';
import { PlusIcon, HistoryIcon, CloseIcon } from './icons/AIIcons';
import useResponsiveTabCount from './hooks/useResponsiveTabCount';
import useMatchMedia from '../../../core/hooks/useMatchMedia';
import { typography, surfaces, borders, textColors, interactive } from '../../../design-system/tokens';

/**
 * AIChatTabs - Tab navigation for switching between recent AI chats
 * Props:
 * - recentChatIds: array of chat IDs to show in tabs
 * - currentChatId: ID of currently active chat
 * - onSelectChat: function(chatId) called when tab is clicked
 * - onNewChat: function() called when "+ New" is clicked
 * - onOpenHistory: function() called when history icon is clicked
 * - collapsed: boolean indicating if chat panel is collapsed
 * - historyButtonRef: ref to attach to history button for dropdown positioning
 */
export const AIChatTabs = ({
  recentChatIds = [],
  currentChatId,
  onSelectChat,
  onNewChat,
  onOpenHistory,
  collapsed,
  historyButtonRef
}) => {
  const { chats, toggleArchive } = useAI();
  const isMobileViewport = useMatchMedia("(max-width: 767px)", false);
  const { maxTabs, containerRef } = useResponsiveTabCount();
  const [hoveredChatId, setHoveredChatId] = useState(null);

  // Get full chat objects for recent IDs
  const recentChats = useMemo(() => {
    const resolved = recentChatIds
      .map(id => chats.find(chat => chat.id === id))
      .filter(Boolean) // Remove any not found
    return isMobileViewport ? resolved : resolved.slice(0, maxTabs); // Limit by responsive max (desktop)
  }, [recentChatIds, chats, maxTabs, isMobileViewport]);

  // Truncate title to max 15 chars
  const truncateTitle = (title) => {
    if (!title) return 'Untitled';
    return title.length > 15 ? title.substring(0, 15) + '...' : title;
  };

  // Handle archive (hide) chat
  const handleArchiveChat = async (e, chatId) => {
    e.stopPropagation(); // Prevent selecting the chat
    await toggleArchive(chatId, true);
  };

  if (isMobileViewport) {
    const hasChats = recentChats.length > 0;
    const selected =
      hasChats && recentChats.some((c) => c.id === currentChatId)
        ? currentChatId
        : hasChats
          ? recentChats[0].id
          : "";

    return (
      <div
        ref={containerRef}
        className={`px-2 py-2 ${surfaces.primary}`}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <select
              aria-label="Recent chats"
              className={`flex-1 min-w-0 px-3 py-2 border rounded-md shadow-sm ${surfaces.primary} ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${borders.default}`}
              value={String(selected)}
              onChange={(e) => {
                const raw = e.target.value;
                if (!raw) return;
                const parsed = Number(raw);
                onSelectChat?.(Number.isFinite(parsed) ? parsed : raw);
              }}
              disabled={!hasChats}
            >
              {!hasChats ? (
                <option value="">No recent chats</option>
              ) : null}
              {recentChats.map((chat) => (
                <option key={chat.id} value={String(chat.id)}>
                  {truncateTitle(chat.title)}
                </option>
              ))}
            </select>

            <button
              ref={historyButtonRef}
              onClick={onOpenHistory}
              className={`p-2 ${textColors.secondary} ${textColors.hover} ${surfaces.hover} rounded-md transition-colors`}
              aria-label="History"
              title="View chat history"
            >
              <HistoryIcon className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={onNewChat}
            className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 ${typography.button} font-medium rounded-md whitespace-nowrap ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText} !text-white`}
            title="Create new chat"
          >
            <PlusIcon className="w-5 h-5" />
            <span>New chat</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`flex items-center gap-1.5 px-2 py-2 ${surfaces.primary}`}
    >
      {/* Tab list */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
        {recentChats.length === 0 ? (
          <div className={`${typography.caption} ${textColors.muted} px-2`}>No recent chats</div>
        ) : (
          recentChats.map(chat => {
            const isActive = chat.id === currentChatId;
            const isHovered = hoveredChatId === chat.id;
            
            return (
              <div
                key={chat.id}
                className="relative flex-shrink-0"
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => setHoveredChatId(null)}
              >
                <button
                  onClick={() => onSelectChat(chat.id)}
                  className={`
                    pl-3 py-1.5 ${typography.caption} font-medium rounded-md transition-all duration-200
                    whitespace-nowrap flex items-center gap-1
                    ${isHovered ? 'pr-8' : 'pr-3'}
                    ${isActive 
                      ? `${interactive.primaryBg} ${interactive.primaryText} !text-white shadow-sm` 
                      : `${surfaces.tertiary} ${textColors.secondary} ${surfaces.hover} hover:shadow-sm`
                    }
                  `}
                  title={chat.title || 'Untitled'}
                >
                  {/* Chat title */}
                  <span>{truncateTitle(chat.title)}</span>
                </button>

                {/* X button - only visible on hover with fade-in animation (sibling, not nested) */}
                {isHovered && (
                  <button
                    type="button"
                    onClick={(e) => handleArchiveChat(e, chat.id)}
                    className={`
                      absolute right-1 top-1/2 -translate-y-1/2
                      p-0.5 rounded transition-all duration-200 animate-fadeIn
                      ${isActive 
                        ? `${interactive.primaryHover} bg-opacity-50` 
                        : `${surfaces.active}`
                      }
                    `}
                    aria-label="Hide chat"
                    title="Hide chat"
                  >
                    <CloseIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Action buttons */}
      <div className={`flex items-center gap-1 flex-shrink-0 pl-1 border-l ${borders.default}`}>
        {/* History button */}
        <button
          ref={historyButtonRef}
          onClick={onOpenHistory}
          className={`p-1.5 ${textColors.secondary} ${textColors.hover} ${surfaces.hover} rounded-md transition-colors`}
          aria-label="History"
          title="View chat history"
        >
          <HistoryIcon className="w-4 h-4" />
        </button>

        {/* + New button */}
        <button
          onClick={onNewChat}
          className={`flex items-center gap-1 px-2.5 py-1.5 ${typography.caption} font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-500 hover:bg-blue-50 hover:dark:bg-blue-950 rounded-md transition-colors`}
          title="Create new chat"
        >
          <PlusIcon className="w-4 h-4" />
          <span>New</span>
        </button>
      </div>
    </div>
  );
};

export default AIChatTabs;

